import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  output,
} from '@angular/core';
import {
  Icon,
  NotImplementedYetService,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableService,
} from '@web/shared/ui';
import { MultimediaListCustomCellTemplateComponent } from '../../components/multimedia-list-custom-cell-template/multimedia-list-custom-cell-template.component';
import { AdminMultimediaListItemAction } from '../../enums/admin-multimedia-list-item-action';
import { AdminMultimediaListCollectionAction } from '../../enums/admin-multimedia-list-collection-action';
import { AdminMultimediaCollectionItem } from '../../models/admin-multimedia-collection-item';
import { AdminMultimediaListFilter } from '../../models/admin-multimedia-list-filter';
import { AdminMultimediaDataSourceService } from '../../services/admin-multimedia-data-source.service';
import { once } from '@web/utils';

@Component({
  selector: 'app-multimedia-list',
  imports: [TableComponent, MultimediaListCustomCellTemplateComponent],
  templateUrl: './multimedia-list.component.html',
  styleUrl: './multimedia-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultimediaListComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminMultimediaCollectionItem, AdminMultimediaListFilter, AdminMultimediaListItemAction, AdminMultimediaListCollectionAction>).create();
  /* prettier-ignore */
  private readonly multimediaDataSource = inject(AdminMultimediaDataSourceService);
  private readonly table = inject(TableService);
  private readonly notImplementedYet = inject(NotImplementedYetService);

  public readonly previewClickEvent = output<AdminMultimediaCollectionItem>();

  protected config!: TableConfig<
    AdminMultimediaCollectionItem,
    AdminMultimediaListFilter,
    AdminMultimediaListItemAction,
    AdminMultimediaListCollectionAction
  >;

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder //
      .dataKey('id')
      .columns((builder) => [
        builder //
          .initSelection()
          .build(),

        builder //
          .initCustom('filename')
          .label('Plik')
          .width('40rem')
          .sortable(true)
          .filterable(true)
          .filterKey('title')
          .sortKey('title')
          .build(),

        builder //
          .initDateTime('createdAt')
          .label('Data utworzenia')
          .sortable(true)
          .filterable(true)
          .filterKey('createdAt')
          .sortKey('createdAt')
          .build(),

        builder //
          .initCustom('size')
          .width('20rem')
          .label('Rozmiar pliku')
          .sortable(true)
          .sortKey('size')
          .build(),
      ])
      .filters((builder) => [
        builder //
          .initText('title')
          .build(),

        builder //
          .initDateRange('createdAt')
          .build(),
      ])
      .itemActions((builder) => [
        builder
          .initCommand(AdminMultimediaListItemAction.Preview)
          .icon(Icon.Eye)
          .tooltip('Podgląd')
          .command((model) => this.notifyPreviewClickEvent(model))
          .build(),

        builder
          .initCommand(AdminMultimediaListItemAction.Remove)
          .visibility((item) => item.canBeRemoved)
          .icon(Icon.Trash)
          .tooltip('Usuń')
          .command((model) => this.confirmAndRemove(model))
          .build(),

        builder
          .initMore(AdminMultimediaListItemAction.More)
          .children((builder) => [
            builder
              .initCommand(AdminMultimediaListItemAction.Download)
              .icon(Icon.CloudDownloadFill)
              .tooltip('Pobierz plik')
              .command(() => this.notImplementedYet.showToast())
              .build(),

            builder
              .initCommand(AdminMultimediaListItemAction.Copy)
              .icon(Icon.Copy)
              .tooltip('Skopiuj adres URL')
              .command(() => this.notImplementedYet.showToast())
              .build(),
          ])
          .build(),
      ])
      .dataSource({
        fetch: (lazyLoad) =>
          this.multimediaDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private confirmAndRemove(model: AdminMultimediaCollectionItem): void {
    this.multimediaDataSource
      .confirmAndRemoveById(model.id)
      .pipe(once())
      .subscribe({
        next: () => this.refresh(),
      });
  }

  private notifyPreviewClickEvent(model: AdminMultimediaCollectionItem): void {
    this.previewClickEvent.emit(model);
  }

  private refresh(): void {
    this.table.refresh();
  }
}
